import { readGateValue } from './gate-store';
import type { FeatureLayer } from './types';

/**
 * What an analytics provider registers: events only. Gates never come from a
 * provider, so a registered layer can't become a second gate source;
 * `analytics.checkGate` reads the gate store directly.
 */
export type TrackLayer = Pick<FeatureLayer, 'track'>;

/** Default before the analytics provider initializes, after teardown, and
 *  whenever analytics consent is withheld: events are dropped. */
const noopTrackLayer: TrackLayer = {
	track: () => {},
};

let currentTrackLayer: TrackLayer = noopTrackLayer;

/**
 * Imperative, vendor-neutral entry point for non-React code (input state
 * machines, utils). `checkGate` and `track` come from different places:
 * gate checks read the gate store (fed by the installed gate client, never by
 * analytics consent), and events go to whatever layer is registered.
 * Always defined; safe to call before init (track no-ops, gates read false).
 */
export const analytics: FeatureLayer = {
	checkGate: name => readGateValue(name),
	track: (event, value, metadata) => currentTrackLayer.track(event, value, metadata),
};

/** Install the live event layer. Called by the adapter when the client is ready. */
export function registerFeatureLayer(layer: TrackLayer): void {
	currentTrackLayer = layer;
}

/** Restore the no-op event layer (teardown / tests). */
export function resetFeatureLayer(): void {
	currentTrackLayer = noopTrackLayer;
}
